import React from 'react';
import { CheckCircle2, Lightbulb, Users, Zap } from 'lucide-react';

// Assumed Icon Component imports from your asset tree
// import { OpportunityIcon, LeadershipIcon, ShapingIcon } from './Icons';

// Specific illustations for early access section
const OpportunityIcon = () => (
    <div className="border-4 border-cyan-500 bg-slate-950 p-6 rounded-3xl w-24 h-24 flex items-center justify-center text-cyan-400 mx-auto">
        <Zap className="w-12 h-12" />
    </div>
);
const LeadershipIcon = () => (
    <div className="border-4 border-cyan-500 bg-slate-950 p-6 rounded-3xl w-24 h-24 flex items-center justify-center text-cyan-400 mx-auto">
        <Users className="w-12 h-12" />
    </div>
);
const ShapingIcon = () => (
    <div className="border-4 border-cyan-500 bg-slate-950 p-6 rounded-3xl w-24 h-24 flex items-center justify-center text-cyan-400 mx-auto">
        <Lightbulb className="w-12 h-12" />
    </div>
);
export default function EarlyAccessBenefitsSection() {
  
  // Isolated Bottom Component rendered exactly once as requested
  function SharedFeatureIcons() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 text-center border-t border-slate-800/80 w-full">
        {[
          { icon: OpportunityIcon, label: 'Access Opportunities' },
          { icon: LeadershipIcon, label: 'Trusted Platform' },
          { icon: ShapingIcon, label: 'Shape The Future' }
        ].map(i => (
          <div key={i.label} className="space-y-3 p-4 rounded-xl bg-slate-900/40 border border-slate-800/60 hover:bg-slate-950/80 hover:border-slate-700/80 transition-all duration-200 group/icon">
            <div className="flex justify-center text-cyan-400 group-hover/icon:text-cyan-300 transition-colors">
              <i.icon />
            </div>
            <p className="text-xs text-slate-400 font-mono tracking-wide">{i.label}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <section className="relative w-full max-w-7xl mx-auto px-4 md:px-8 py-16 flex flex-col gap-8 bg-slate-950 text-slate-100 rounded-3xl border border-slate-900 overflow-hidden">
      
      {/* Ambient background accent rings to fit Premium Cyber-Dark UI */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[150px] bg-cyan-500/5 blur-[100px] pointer-events-none" />

      {/* 1ST BOX: Top Full-Width Header Box Panel */}
      <div className="w-full border border-slate-800 bg-slate-900/40 p-8 md:p-10 rounded-2xl text-center shadow-lg shadow-slate-950/40">
      <h3 className="text-3xl font-bold text-slate-50 text-center lg:text-center">Why Join Early Access?</h3>

        <p className="text-xl md:text-2xl text-slate-300 max-w-5xl mx-auto leading-relaxed font-normal tracking-wide">
          Join the Founding Members of India's Next Private Leadership Platform. Gain early access, exclusive visibility, and direct connections before public launch
        </p>
        <p className="text-base text-slate-400 leading-relaxed">
            We are in early access to build the best experience for both leaders and organizations. As an early member, you get:
          </p>
      </div>

      {/* 2ND BOX SYSTEM: Parallel Benefits Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch w-full">
        
        {/* Left Column: Candidates Benefits (Light Translucent Blue Tint) */}
        <div className="border border-blue-500/20 bg-gradient-to-b from-blue-950/20 to-slate-900/40 p-8 md:p-10 rounded-2xl flex flex-col space-y-6 hover:border-blue-400/40 transition-all duration-300 shadow-md">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent">
            Candidates Benefits
          </h3>
          
          <ul className="space-y-3 text-slate-300 text-sm md:text-base leading-relaxed flex-grow">
            {[
              "Founding Member 2026 badge with priority profile visibility",
              "Private access to leadership opportunities before public release",
              "Direct visibility to CEOs, Founders, CXOs, and hiring decision-makers",
              "Invitation-only executive talent platform",
              "Confidential career exploration without public exposure",
              "Priority consideration for strategic leadership roles",
              "Verified professional community focused on quality over quantity",
              "Early influence on platform features and future direction",
              "Access to exclusive leadership insights and hiring intelligence",
              "Priority support during onboarding and profile setup"
            ].map(item => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right Column: Recruiters Benefits (Light Translucent Purple Tint) */}
        <div className="border border-purple-500/20 bg-gradient-to-b from-purple-950/20 to-slate-900/40 p-8 md:p-10 rounded-2xl flex flex-col space-y-6 hover:border-purple-400/40 transition-all duration-300 shadow-md">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-purple-300 bg-clip-text text-transparent">
            Recruiters Benefits
          </h3>
          
          <ul className="space-y-3 text-slate-300 text-sm md:text-base leading-relaxed flex-grow">
            {[
              "Founding Member 2026 badge with enhanced employer visibility",
              "Priority access to curated leadership talent before competitors",
              "Direct connections with experienced executives and decision-makers",
              "Reduced hiring timelines for critical leadership positions",
              "Private and confidential executive hiring environment",
              "Premium employer branding within a trusted leadership platform",
              "Early influence on platform development and hiring workflows",
              "Priority support from the founding team",
              "Exclusive access to invitation-only leadership talent pools",
              "Preferred access to future premium platform capabilities"
            ].map(item => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>

      </div>

      {/* 3RD BOX: Dedicated Full-Width Footer Features Layout */}
      <div className="w-full border border-slate-800 bg-slate-900/20 p-6 md:p-8 rounded-2xl shadow-inner">
        <SharedFeatureIcons />
      </div>

    </section>
  );
}